/**
 * NomenclatureEnginePage — /admin/nomenclature-engine
 *
 * Live, interactive nomenclature engine for the VOS production pipeline.
 *
 * Three workbenches on one page:
 *   1. Validator        — paste any filename, see parse breakdown + errors
 *   2. Builder          — pick project / entity / task / version → get canonical filename
 *   3. Pickup-safety    — simulate "TEX artist opening RIG file" → see the verdict
 *
 * Below: reference cards for task / stage / status codes + pipeline DAG.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import {
  ShieldCheck, ShieldAlert, ShieldX, FileText, Copy, Check,
  ArrowRight, Layers, Wrench, Beaker, Lock, Unlock, Save,
} from 'lucide-react';

const PROJECT_PLACEHOLDER = 'JKB';
const ENTITY_PLACEHOLDER  = '010';

const VERDICT_TINTS = {
  ok:    { Icon: ShieldCheck, bg: 'bg-emerald-500/10', text: 'text-emerald-300', border: 'border-emerald-500/40', label: 'SAFE' },
  warn:  { Icon: ShieldAlert, bg: 'bg-amber-500/10',   text: 'text-amber-300',   border: 'border-amber-500/40',   label: 'WARN' },
  block: { Icon: ShieldX,     bg: 'bg-rose-500/10',    text: 'text-rose-300',    border: 'border-rose-500/40',    label: 'BLOCKED' },
};

export default function NomenclatureEnginePage() {
  const { api, user } = useAuth();
  const [spec, setSpec] = useState(null);

  // Workbench 1: validator
  const [vInput, setVInput] = useState('JKB_MONSTER_RIG_v004_APR.blend');
  const [vParse, setVParse] = useState(null);

  // Workbench 2: builder
  const [bProj, setBProj] = useState(PROJECT_PLACEHOLDER);
  const [bEnt,  setBEnt]  = useState('MONSTER');
  const [bTask, setBTask] = useState('RIG');
  const [bStage, setBStage] = useState('');
  const [bStatus, setBStatus] = useState('');
  const [bVer,  setBVer]  = useState(1);
  const [bOut,  setBOut]  = useState('');
  const [copied, setCopied] = useState(false);

  // Workbench 3: pickup-safety simulator
  const [pFile, setPFile] = useState('JKB_MONSTER_RIG_v004.blend');
  const [pTask, setPTask] = useState('TEX');
  const [pVerdict, setPVerdict] = useState(null);

  useEffect(() => { api.get('/nomenclature/spec').then(r => setSpec(r.data)); }, [api]);

  // Live validator (debounce-ish via React's batching)
  useEffect(() => {
    if (!vInput.trim()) { setVParse(null); return; }
    const t = setTimeout(() => {
      api.post('/nomenclature/parse', { filename: vInput })
        .then(r => setVParse(r.data)).catch(() => setVParse(null));
    }, 150);
    return () => clearTimeout(t);
  }, [api, vInput]);

  // Founder spec May 25, 2026: default extension is per-discipline,
  // NOT always .blend. Episode-level deliverables (Animatics / Storyboard
  // / Edit / Sound / Final) and video-shippable disciplines emit .mp4 by
  // default. Image disciplines emit .png. Source-file disciplines stay
  // on .blend. The Builder also exposes an Extension picker so a power
  // user can override (e.g. an Edit task using .mov).
  // Founder bug Feb 2026: Animatics-family tasks (STB, ANI, CBA) were
  // defaulting to .blend because CBA wasn't mapped — they're VIDEO
  // outputs (.mov/.mp4), not source files. Editorial outputs (EDT, FNL)
  // are also video. Source-file disciplines (Blender) stay on .blend.
  const DEFAULT_EXT_FOR_TASK = {
    // Video / episode-level outputs — animatics family + editorial
    STB: '.mp4', ANI: '.mp4', CBA: '.mp4',
    EDT: '.mp4', FNL: '.mp4',
    // Audio
    SND: '.wav',
    // Image disciplines (frame renders)
    LGT: '.png', CMP: '.png', TEX: '.png',
    // Source-file disciplines (Blender pipeline)
    MOD: '.blend', UV: '.blend', RIG: '.blend',
    HAIR: '.blend', CLO: '.blend', FX: '.blend',
    LAY: '.blend', ANM: '.blend',
  };
  const builderExt = DEFAULT_EXT_FOR_TASK[bTask] || '.blend';

  // Live builder
  useEffect(() => {
    if (!bProj || !bEnt || !bTask) { setBOut(''); return; }
    const t = setTimeout(() => {
      api.post('/nomenclature/build', {
        project: bProj, entity: bEnt, task: bTask, version: Number(bVer) || 1,
        stage: bStage || null, status: bStatus || null, ext: builderExt,
      }).then(r => setBOut(r.data.filename)).catch(() => setBOut(''));
    }, 150);
    return () => clearTimeout(t);
  }, [api, bProj, bEnt, bTask, bStage, bStatus, bVer, builderExt]);

  // Live pickup-safety
  useEffect(() => {
    if (!pFile || !pTask) { setPVerdict(null); return; }
    const t = setTimeout(() => {
      api.post('/nomenclature/check-pickup', { filename: pFile, my_task: pTask })
        .then(r => setPVerdict(r.data)).catch(() => setPVerdict(null));
    }, 150);
    return () => clearTimeout(t);
  }, [api, pFile, pTask]);

  const tintForVerdict = pVerdict ? VERDICT_TINTS[pVerdict.level] || VERDICT_TINTS.warn : null;

  const handleCopy = useCallback(() => {
    if (!bOut) return;
    navigator.clipboard.writeText(bOut).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1200);
    });
  }, [bOut]);

  if (!user) return null;

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-100" data-testid="nomenclature-engine-page">
      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-semibold tracking-tight flex items-center gap-2">
            <FileText className="w-6 h-6 text-sky-400" />
            Nomenclature Engine
          </h1>
          <p className="text-sm text-zinc-400 mt-1 max-w-3xl">
            Active naming + pickup safety. Filenames follow{' '}
            <span className="font-mono text-emerald-300">PROJECT_ENTITY_TASK_v###</span>.
            The pickup checker prevents the today's-disaster scenario: a TEX artist
            saving over a RIG file and wiping skinning data.
          </p>
        </div>

        {/* 🆕 Workflow overview — how the 4 pieces connect */}
        <div className="rounded-xl border border-sky-500/30 bg-sky-500/5 p-5 mb-8" data-testid="workflow-overview">
          <p className="text-[12px] uppercase tracking-wider text-sky-400 mb-3">How this protects your studio</p>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3 text-[12px]">
            <div className="flex gap-2">
              <span className="font-mono text-sky-300 text-lg">1.</span>
              <div>
                <p className="text-zinc-200 font-medium">You set project code</p>
                <p className="text-zinc-500 text-[11px] mt-0.5">Down below ⬇ — "Hansel & Gretel" → HNG. Locks the first segment of every filename.</p>
              </div>
            </div>
            <div className="flex gap-2">
              <span className="font-mono text-sky-300 text-lg">2.</span>
              <div>
                <p className="text-zinc-200 font-medium">VOS builds canonical names</p>
                <p className="text-zinc-500 text-[11px] mt-0.5">Every task auto-generates its expected filename. e.g. HNG_HERO_RIG_v001.blend</p>
              </div>
            </div>
            <div className="flex gap-2">
              <span className="font-mono text-sky-300 text-lg">3.</span>
              <div>
                <p className="text-zinc-200 font-medium">Blender enforces saves</p>
                <p className="text-zinc-500 text-[11px] mt-0.5">N-panel addon "Save Next Version" button uses the canonical name. Artists can't choose.</p>
              </div>
            </div>
            <div className="flex gap-2">
              <span className="font-mono text-sky-300 text-lg">4.</span>
              <div>
                <p className="text-zinc-200 font-medium">Agent auto-renames bad saves</p>
                <p className="text-zinc-500 text-[11px] mt-0.5">Last line of defence — any wrongly named .blend in C:\VOS_Work gets renamed by the desktop agent.</p>
              </div>
            </div>
          </div>
        </div>

        {/* 🆕 Source of truth — moved up since it's the foundation */}
        <ProjectCodesPanel />

        {/* 🆕 Nomenclature LOCK — admin-only universal shot template
            (founder spec Feb 12 2026 — Rate-Card style locking system). */}
        <NomenclatureLockPanel api={api} userRole={user?.role || ''} />

        {/* Workbenches grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-10">
          {/* 1. VALIDATOR */}
          <div className="rounded-xl border border-zinc-800 bg-zinc-900/40 p-5">
            <div className="flex items-center gap-2 mb-3">
              <Beaker className="w-4 h-4 text-sky-400" />
              <h3 className="text-sm font-semibold">1. Validator</h3>
            </div>
            <p className="text-[11px] text-zinc-500 mb-3">Paste a filename. We parse it and surface every issue.</p>
            <input
              data-testid="nom-validator-input"
              value={vInput}
              onChange={(e) => setVInput(e.target.value)}
              placeholder="JKB_010_ANM_v003.blend"
              className="w-full bg-zinc-950 border border-zinc-700 rounded-md px-3 py-2 text-[13px] font-mono"
            />
            <div className="mt-3 min-h-[120px]">
              {vParse ? (
                vParse.valid ? (
                  <div className="space-y-1 text-[12px]" data-testid="nom-validator-result">
                    <div className="flex items-center gap-2 text-emerald-300 font-medium mb-2">
                      <ShieldCheck className="w-4 h-4" /> Valid filename
                    </div>
                    <KV k="Project" v={vParse.project} />
                    <KV k="Entity"  v={vParse.entity} />
                    <KV k="Task"    v={`${vParse.task} (${(spec?.tasks || []).find(t => t.code === vParse.task)?.label || ''})`} />
                    {vParse.stage  && <KV k="Stage"  v={vParse.stage} />}
                    <KV k="Version" v={`v${String(vParse.version).padStart(3, '0')}`} />
                    {vParse.status && <KV k="Status" v={vParse.status} />}
                    {vParse.ext    && <KV k="Ext"    v={vParse.ext} />}
                  </div>
                ) : (
                  <div className="text-[12px]" data-testid="nom-validator-result">
                    <div className="flex items-center gap-2 text-rose-300 font-medium mb-2">
                      <ShieldX className="w-4 h-4" /> Invalid
                    </div>
                    <ul className="space-y-1 text-rose-200">
                      {vParse.issues.map((i, idx) => <li key={idx}>• {i}</li>)}
                    </ul>
                  </div>
                )
              ) : <p className="text-[11px] text-zinc-600">Type a filename to begin…</p>}
            </div>
          </div>

          {/* 2. BUILDER */}
          <div className="rounded-xl border border-zinc-800 bg-zinc-900/40 p-5">
            <div className="flex items-center gap-2 mb-3">
              <Wrench className="w-4 h-4 text-emerald-400" />
              <h3 className="text-sm font-semibold">2. Builder</h3>
            </div>
            <p className="text-[11px] text-zinc-500 mb-3">Generate a canonical filename. Use this when starting a new file.</p>
            {/* Founder spec May 25, 2026: episode-level disciplines
                (Animatics / Storyboard / Edit / Sound / Final) work at
                episode scope, NOT shot scope — their filenames have no
                entity segment. Disable the Entity field for these and
                show a small hint so admins understand the format. */}
            {(() => {
              const EPISODE_LEVEL = new Set(['ANI', 'STB', 'EDT', 'SND', 'FNL']);
              const isEpisode = EPISODE_LEVEL.has(bTask);
              return (
                <>
                  <div className="grid grid-cols-2 gap-2 mb-3">
                    <Field label="Project"  value={bProj} onChange={setBProj}  placeholder="JKB" mono />
                    <Field
                      label={isEpisode ? 'Entity (n/a)' : 'Entity'}
                      value={isEpisode ? '' : bEnt}
                      onChange={isEpisode ? () => {} : setBEnt}
                      placeholder={isEpisode ? '— episode-level —' : '010 or HERO'}
                      mono
                      disabled={isEpisode}
                    />
                    <Select label="Task" value={bTask} onChange={setBTask} options={(spec?.tasks || []).map(t => ({ value: t.code, label: `${t.code} — ${t.label}` }))} />
                    <Field label="Version" value={bVer} onChange={(v) => setBVer(Number(v) || 1)} placeholder="1" type="number" mono />
                    <Select label="Stage (optional)" value={bStage} onChange={setBStage} options={[{ value: '', label: '— none —' }, ...(spec?.stages || []).map(s => ({ value: s.code, label: `${s.code} — ${s.label}` }))]} />
                    <Select label="Status (optional)" value={bStatus} onChange={setBStatus} options={[{ value: '', label: '— none —' }, ...(spec?.statuses || []).map(s => ({ value: s.code, label: `${s.code} — ${s.label}` }))]} />
                  </div>
                  {isEpisode && (
                    <p className="text-[10px] text-amber-300 -mt-1 mb-2 flex items-center gap-1">
                      <Wrench className="w-3 h-3" /> {bTask} is episode-level — entity segment is omitted.
                    </p>
                  )}
                  <p className="text-[10px] text-zinc-500 mb-2">
                    Default extension for <span className="font-mono text-zinc-300">{bTask || '—'}</span>:
                    <span className="font-mono text-emerald-300 ml-1">{builderExt}</span>
                  </p>
                </>
              );
            })()}
            <div className="rounded-md bg-zinc-950 border border-emerald-500/30 px-3 py-2 flex items-center justify-between gap-2">
              <span className="font-mono text-[13px] text-emerald-300 truncate" data-testid="nom-builder-out">{bOut || '—'}</span>
              <button
                onClick={handleCopy}
                disabled={!bOut}
                data-testid="nom-builder-copy"
                className="text-[11px] inline-flex items-center gap-1 px-2 py-1 rounded bg-zinc-900 border border-zinc-800 hover:bg-zinc-800 disabled:opacity-40"
              >
                {copied ? <Check className="w-3 h-3" /> : <Copy className="w-3 h-3" />}
                {copied ? 'Copied' : 'Copy'}
              </button>
            </div>
          </div>

          {/* 3. PICKUP SAFETY */}
          <div className="rounded-xl border border-zinc-800 bg-zinc-900/40 p-5">
            <div className="flex items-center gap-2 mb-3">
              <Layers className="w-4 h-4 text-rose-400" />
              <h3 className="text-sm font-semibold">3. Pickup Safety</h3>
            </div>
            <p className="text-[11px] text-zinc-500 mb-3">
              Simulate an artist opening a file. The engine returns BLOCK if the
              save would overwrite upstream/approved work.
            </p>
            <input
              data-testid="nom-pickup-file"
              value={pFile}
              onChange={(e) => setPFile(e.target.value)}
              placeholder="JKB_MONSTER_RIG_v004.blend"
              className="w-full bg-zinc-950 border border-zinc-700 rounded-md px-3 py-2 text-[12px] font-mono mb-2"
            />
            <Select
              label="My task assignment"
              value={pTask}
              onChange={setPTask}
              options={(spec?.tasks || []).map(t => ({ value: t.code, label: `${t.code} — ${t.label}` }))}
            />
            <div className="mt-3 min-h-[120px]">
              {pVerdict && tintForVerdict ? (
                <div className={`rounded-md border ${tintForVerdict.border} ${tintForVerdict.bg} p-3`} data-testid="nom-pickup-verdict">
                  <div className={`flex items-center gap-2 text-[12px] font-medium ${tintForVerdict.text} mb-1`}>
                    <tintForVerdict.Icon className="w-4 h-4" /> {tintForVerdict.label}
                  </div>
                  <p className="text-[11px] text-zinc-200">{pVerdict.reason}</p>
                  {pVerdict.suggested_filename && (
                    <div className="mt-2 text-[11px] text-zinc-400">
                      Recommended branch: <span className="font-mono text-emerald-300">{pVerdict.suggested_filename}</span>
                    </div>
                  )}
                </div>
              ) : <p className="text-[11px] text-zinc-600">Fill in to simulate…</p>}
            </div>
          </div>
        </div>

        {/* Source of truth — project codes table */}
        <div id="project-codes-anchor" />

        {/* Reference: tasks + pipeline */}
        {spec && (
          <section className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <ReferenceCard title="Task codes" subtitle="Use ONLY these. Bloat is the enemy.">
              <div className="grid grid-cols-2 gap-1.5 text-[12px]">
                {spec.tasks.map(t => (
                  <div key={t.code} className="flex items-center gap-2 py-1">
                    <span className="font-mono text-sky-300 w-10">{t.code}</span>
                    <span className="text-zinc-300">{t.label}</span>
                    <span className="text-[10px] text-zinc-500 ml-auto">{t.kind}</span>
                  </div>
                ))}
              </div>
            </ReferenceCard>
            <ReferenceCard title="Pipeline dependencies" subtitle="What each task depends on (upstream).">
              <div className="text-[12px] space-y-1.5">
                {Object.entries(spec.pipeline).map(([task, upstream]) => (
                  <div key={task} className="flex items-center gap-2">
                    <span className="font-mono text-emerald-300 w-12">{task}</span>
                    <ArrowRight className="w-3 h-3 text-zinc-600" />
                    <span className="text-zinc-400 font-mono text-[11px]">
                      {upstream.length === 0 ? '— root —' : upstream.join(', ')}
                    </span>
                  </div>
                ))}
              </div>
            </ReferenceCard>
            <ReferenceCard title="Stage suffixes (optional)" subtitle="Append when an artist needs to track a sub-phase.">
              <div className="grid grid-cols-2 gap-1.5 text-[12px]">
                {spec.stages.map(s => (
                  <div key={s.code} className="flex items-center gap-2">
                    <span className="font-mono text-amber-300 w-12">{s.code}</span>
                    <span className="text-zinc-300">{s.label}</span>
                  </div>
                ))}
              </div>
            </ReferenceCard>
            <ReferenceCard title="Status suffixes" subtitle="Appended AFTER version. Locks file once APR/FNL.">
              <div className="grid grid-cols-2 gap-1.5 text-[12px]">
                {spec.statuses.map(s => (
                  <div key={s.code} className="flex items-center gap-2">
                    <span className="font-mono text-zinc-200 w-12">{s.code}</span>
                    <span className="text-zinc-400">{s.label}</span>
                  </div>
                ))}
              </div>
            </ReferenceCard>
          </section>
        )}
      </div>
    </div>
  );
}

function Field({ label, value, onChange, placeholder, type = 'text', mono = false, disabled = false }) {
  return (
    <label className="block">
      <span className="text-[10px] uppercase tracking-wider text-zinc-500">{label}</span>
      <input
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        disabled={disabled}
        className={`mt-0.5 w-full bg-zinc-950 border border-zinc-700 rounded-md px-2 py-1.5 text-[12px] ${mono ? 'font-mono' : ''} ${disabled ? 'opacity-40 cursor-not-allowed' : ''}`}
      />
    </label>
  );
}

function Select({ label, value, onChange, options }) {
  return (
    <label className="block col-span-2">
      <span className="text-[10px] uppercase tracking-wider text-zinc-500">{label}</span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="mt-0.5 w-full bg-zinc-950 border border-zinc-700 rounded-md px-2 py-1.5 text-[12px]"
      >
        {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </label>
  );
}

function KV({ k, v }) {
  return (
    <div className="flex items-center gap-2">
      <span className="text-zinc-500 w-16">{k}</span>
      <span className="font-mono text-zinc-200">{v}</span>
    </div>
  );
}

function ReferenceCard({ title, subtitle, children }) {
  return (
    <div className="rounded-xl border border-zinc-800 bg-zinc-900/40 p-5">
      <h3 className="text-sm font-semibold text-zinc-200">{title}</h3>
      <p className="text-[11px] text-zinc-500 mb-3">{subtitle}</p>
      {children}
    </div>
  );
}

/**
 * ProjectCodesPanel — admins set/edit each project's canonical 3-char
 * code (the SOURCE OF TRUTH). Without this, the nomenclature engine has
 * nothing to anchor to.
 */
function ProjectCodesPanel() {
  const { api } = useAuth();
  const [rows, setRows] = React.useState([]);
  const [editing, setEditing] = React.useState({});  // {project_id: 'NEWCODE'}
  const [loading, setLoading] = React.useState(true);
  const [msg, setMsg] = React.useState('');
  const [savedFor, setSavedFor] = React.useState(null);  // project id that was just saved

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get('/projects');
      const list = (r.data?.projects || r.data || []).map(p => ({
        id: p.id, name: p.name, code: p.code || '', task_count: p.task_count,
      }));
      setRows(list);
    } catch (e) {
      setMsg(e?.response?.data?.detail || 'Failed to load projects');
    } finally { setLoading(false); }
  }, [api]);
  React.useEffect(() => { load(); }, [load]);

  const save = async (id, code, force = false) => {
    setMsg('');
    try {
      const r = await api.post(`/projects/${id}/code`, { code, force });
      setEditing(e => { const n = { ...e }; delete n[id]; return n; });
      const proj = rows.find(x => x.id === id);
      setSavedFor(id);
      setMsg(`✅ Code saved! "${proj?.name}" is now locked as ${r.data.code}. ` +
             `All new filenames will start with "${r.data.code}_". ` +
             `Use the Validator below to test names, or the Builder to generate them.`);
      setTimeout(() => setSavedFor(null), 5000);
      load();
    } catch (e) {
      const detail = e?.response?.data?.detail || 'Failed';
      // If the error is "would break existing filenames", offer force option
      if (e?.response?.status === 409 && /existing filename/i.test(detail)) {
        if (window.confirm(`${detail}\n\nForce the change anyway? This breaks every filename built so far. Continue?`)) {
          return save(id, code, true);
        }
      }
      setMsg(`⚠ ${detail}`);
    }
  };

  const backfill = async () => {
    setMsg('Running backfill…');
    try {
      const r = await api.post('/admin/projects/backfill-codes', {});
      setMsg(`Backfilled ${r.data.updated} projects.`);
      load();
    } catch (e) { setMsg(e?.response?.data?.detail || 'Backfill failed'); }
  };

  return (
    <section className="mb-10 mt-2" data-testid="project-codes-panel">
      <div className="flex items-center justify-between mb-3">
        <div>
          <h2 className="text-sm font-semibold text-zinc-300 flex items-center gap-2">
            <FileText className="w-4 h-4 text-emerald-400" />
            Project codes — source of truth
          </h2>
          <p className="text-[11px] text-zinc-500">
            The first segment of every filename. Lock per project. Changing it after tasks exist breaks every saved filename.
          </p>
        </div>
        <button
          onClick={backfill}
          data-testid="backfill-codes-btn"
          className="text-[11px] inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-zinc-900 border border-zinc-800 hover:bg-zinc-800"
        >
          Auto-fill missing
        </button>
      </div>
      {msg && <div className={`rounded-md border px-3 py-2 text-[12px] mb-3 ${
        msg.startsWith('✅')
          ? 'border-emerald-500/40 bg-emerald-500/10 text-emerald-200'
          : 'border-amber-500/30 bg-amber-500/10 text-amber-200'
      }`} data-testid="project-codes-msg">{msg}</div>}
      <div className="rounded-xl border border-zinc-800 overflow-hidden">
        <table className="w-full text-[13px]">
          <thead className="bg-zinc-900/80 text-[11px] uppercase tracking-wider text-zinc-500">
            <tr>
              <th className="text-left px-4 py-2 font-medium">Project</th>
              <th className="text-left px-4 py-2 font-medium">Code</th>
              <th className="text-left px-4 py-2 font-medium">Example filename</th>
              <th className="text-right px-4 py-2 font-medium"></th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={4} className="px-4 py-6 text-center text-zinc-500">Loading…</td></tr>}
            {!loading && rows.length === 0 && <tr><td colSpan={4} className="px-4 py-6 text-center text-zinc-500">No projects yet</td></tr>}
            {rows.map(r => {
              const isEditing = r.id in editing;
              const codeValue = isEditing ? editing[r.id] : r.code;
              return (
                <tr key={r.id} className={`border-t border-zinc-800 hover:bg-zinc-900/40 ${savedFor === r.id ? 'bg-emerald-500/5' : ''}`} data-testid={`project-code-row-${r.id}`}>
                  <td className="px-4 py-2 text-zinc-200">{r.name}</td>
                  <td className="px-4 py-2">
                    <input
                      value={codeValue}
                      onChange={(e) => setEditing(ed => ({ ...ed, [r.id]: e.target.value.toUpperCase().slice(0, 10) }))}
                      className="bg-zinc-950 border border-zinc-700 rounded-md px-2 py-1 text-[12px] font-mono w-24"
                      placeholder="HAG"
                      data-testid={`project-code-input-${r.id}`}
                    />
                  </td>
                  <td className="px-4 py-2 text-[11px] font-mono text-zinc-400">
                    {codeValue ? `${codeValue}_010_ANM_v001.blend` : '— set code to preview —'}
                  </td>
                  <td className="px-4 py-2 text-right">
                    {isEditing && (
                      <button
                        onClick={() => save(r.id, codeValue)}
                        disabled={!codeValue || codeValue === r.code}
                        data-testid={`project-code-save-${r.id}`}
                        className="text-[11px] px-3 py-1 rounded-md bg-emerald-500/20 border border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/30 disabled:opacity-40"
                      >
                        Save
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
}


/**
 * NomenclatureLockPanel — admin-managed universal SHOT NAMING TEMPLATE.
 *
 * Founder spec Feb 12 2026 ("FINAL RATE CARD like locking system"):
 *   - Admin sets the template ONCE.
 *   - Template is the single source of truth for ALL canonical name
 *     generation + validation across ALL projects.
 *   - Locked by default. Unlocking is admin-only + audited.
 *
 * Settings exposed:
 *   - shot_entity.prefix          (default "SH")
 *   - shot_entity.pad_width        (default 3 — "SH001"; set 4 for "SH0001")
 *   - shot_entity.reject_separators (default true — blocks SH_001, SH-001)
 *   - version_pad                  (default 3 — "v001"; 4 for "v0001")
 *
 * Behaviour:
 *   - GET /api/nomenclature/template — any role.
 *   - PUT /api/nomenclature/template — master_admin only.
 *   - POST /api/nomenclature/template/unlock — master_admin only.
 *
 * Non-admin roles still see the panel (read-only) so PMs/supervisors
 * know what the locked rule is.
 */
function NomenclatureLockPanel({ api, userRole }) {
  const isAdmin = ['master_admin', 'super_admin'].includes((userRole || '').toLowerCase());
  const [tpl, setTpl] = useState(null);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(0);
  const [err, setErr] = useState('');

  // Form state — populated from server then editable
  const [prefix, setPrefix] = useState('SH');
  const [padWidth, setPadWidth] = useState(3);
  const [rejectSeparators, setRejectSeparators] = useState(true);
  const [versionPad, setVersionPad] = useState(3);

  const loadTemplate = useCallback(async () => {
    try {
      const r = await api.get('/nomenclature/template');
      const t = r.data?.template || {};
      setTpl(t);
      const se = t.shot_entity || {};
      setPrefix(se.prefix || 'SH');
      setPadWidth(Number(se.pad_width || 3));
      setRejectSeparators(se.reject_separators !== false);
      setVersionPad(Number(t.version_pad || 3));
    } catch (e) {
      setErr(e?.response?.data?.detail || e?.message || 'Load failed');
    }
  }, [api]);

  useEffect(() => { loadTemplate(); }, [loadTemplate]);

  const exampleCanonical = useMemo(() => {
    const ent = `${prefix}${String(1).padStart(padWidth, '0')}`;
    const ver = `v${String(1).padStart(versionPad, '0')}`;
    return `TLM_${ent}_LAY_${ver}.blend`;
  }, [prefix, padWidth, versionPad]);

  const handleSave = async () => {
    if (!isAdmin) return;
    setSaving(true); setErr('');
    try {
      const regex = `^${prefix}\\d{${Math.max(1, padWidth - 1)},${padWidth + 1}}$`;
      const r = await api.put('/nomenclature/template', {
        shot_entity: { prefix, pad_width: padWidth, regex,
                        reject_separators: rejectSeparators },
        version_pad: versionPad,
      });
      setTpl(r.data?.template || null);
      setSavedAt(Date.now());
      setTimeout(() => setSavedAt(0), 2500);
    } catch (e) {
      setErr(e?.response?.data?.detail || e?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleUnlock = async () => {
    if (!isAdmin) return;
    if (!window.confirm(
      'Unlocking lets you change SH### → SH#### (etc) which will break ' +
      'every existing canonical filename in the database. Continue?')) return;
    try {
      await api.post('/nomenclature/template/unlock');
      await loadTemplate();
    } catch (e) {
      setErr(e?.response?.data?.detail || e?.message || 'Unlock failed');
    }
  };

  return (
    <section
      className="rounded-xl border border-amber-500/30 bg-amber-500/5 p-5 mb-8"
      data-testid="nomenclature-lock-panel"
    >
      <header className="flex items-start justify-between gap-4 mb-4 flex-wrap">
        <div className="flex items-start gap-3">
          {tpl?.locked
            ? <Lock className="w-5 h-5 text-amber-300 mt-0.5" />
            : <Unlock className="w-5 h-5 text-rose-300 mt-0.5" />}
          <div>
            <h2 className="text-[14px] font-semibold uppercase tracking-wider text-amber-200">
              {tpl?.locked ? 'Shot Naming · LOCKED' : 'Shot Naming · UNLOCKED'}
            </h2>
            <p className="text-[11.5px] text-amber-100/70 mt-1 max-w-2xl leading-snug">
              Universal SHOT entity format used by every project. Like the
              Rate Card: set once, locks every canonical filename and
              every upload validation across all production.
            </p>
            {tpl?.locked_by && (
              <p className="text-[10.5px] text-amber-200/50 mt-1.5 font-mono">
                v{tpl?.version} · locked by {tpl.locked_by}
                {tpl.locked_at && ` · ${new Date(tpl.locked_at).toLocaleDateString()}`}
              </p>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          {tpl?.locked && isAdmin && (
            <button
              data-testid="nom-lock-unlock-btn"
              onClick={handleUnlock}
              className="text-[10.5px] px-3 py-1.5 rounded border border-rose-500/40 text-rose-300 hover:bg-rose-500/10 uppercase tracking-wider font-bold"
              title="Unlock to allow shape changes — will affect every project"
            >
              Unlock
            </button>
          )}
        </div>
      </header>

      {/* The editable form */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
        <NomLockField label="Shot Prefix" hint="Two letters preceding the digits">
          <input
            data-testid="nom-lock-prefix-input"
            value={prefix}
            onChange={(e) => setPrefix(e.target.value.toUpperCase().replace(/[^A-Z]/g, '').slice(0, 5))}
            disabled={!isAdmin}
            className="w-full bg-zinc-950 border border-zinc-700 rounded-md px-3 py-2 text-[13px] font-mono uppercase disabled:opacity-50"
          />
        </NomLockField>
        <NomLockField label="Digit Padding" hint="3 → SH001  ·  4 → SH0001">
          <select
            data-testid="nom-lock-pad-select"
            value={padWidth}
            onChange={(e) => setPadWidth(parseInt(e.target.value, 10))}
            disabled={!isAdmin}
            className="w-full bg-zinc-950 border border-zinc-700 rounded-md px-3 py-2 text-[13px] font-mono disabled:opacity-50"
          >
            {[2, 3, 4, 5].map(n => <option key={n} value={n}>{n} digits</option>)}
          </select>
        </NomLockField>
        <NomLockField label="Version Padding" hint="3 → v001  ·  4 → v0001">
          <select
            data-testid="nom-lock-vpad-select"
            value={versionPad}
            onChange={(e) => setVersionPad(parseInt(e.target.value, 10))}
            disabled={!isAdmin}
            className="w-full bg-zinc-950 border border-zinc-700 rounded-md px-3 py-2 text-[13px] font-mono disabled:opacity-50"
          >
            {[2, 3, 4].map(n => <option key={n} value={n}>{n} digits</option>)}
          </select>
        </NomLockField>
        <NomLockField label="Reject Separators" hint="Blocks SH_001 and SH-001">
          <label className="flex items-center gap-2 mt-2 cursor-pointer">
            <input
              type="checkbox"
              data-testid="nom-lock-strict-input"
              checked={rejectSeparators}
              onChange={(e) => setRejectSeparators(e.target.checked)}
              disabled={!isAdmin}
              className="w-4 h-4 accent-amber-500"
            />
            <span className="text-[12px] text-zinc-300">
              {rejectSeparators ? 'STRICT — no `_` or `-` inside entity' : 'LOOSE — allow separators'}
            </span>
          </label>
        </NomLockField>
      </div>

      {/* Canonical example preview */}
      <div className="rounded-lg bg-zinc-950/60 border border-zinc-800 p-4 mb-4">
        <p className="text-[10.5px] uppercase tracking-wider text-zinc-500 mb-2">
          Live preview · this is the canonical name every artist will see
        </p>
        <p className="font-mono text-[16px] text-emerald-300 break-all"
           data-testid="nom-lock-example">
          {exampleCanonical}
        </p>
        <p className="mt-2 text-[10.5px] text-zinc-500 italic">
          The strict gate rejects any deviation (e.g. <code className="text-rose-300">TLM_SH_001_LAY_v001.blend</code>) and asks the artist to rename.
        </p>
      </div>

      {err && (
        <p className="text-[11.5px] text-rose-300 mb-3"
           data-testid="nom-lock-err">{err}</p>
      )}

      <div className="flex items-center gap-3">
        {isAdmin ? (
          <button
            data-testid="nom-lock-save-btn"
            onClick={handleSave}
            disabled={saving}
            className="px-4 py-2 rounded-md bg-amber-500 hover:bg-amber-400 text-zinc-950 text-[12px] uppercase tracking-wider font-bold flex items-center gap-1.5 disabled:opacity-50"
          >
            {saving ? <span>Saving…</span> : <><Save className="w-4 h-4" /> Save & Lock</>}
          </button>
        ) : (
          <span className="text-[11.5px] text-zinc-500 italic">
            Read-only — only master_admin can change the universal template.
          </span>
        )}
        {savedAt > 0 && (
          <span className="text-[11.5px] text-emerald-300 flex items-center gap-1"
                data-testid="nom-lock-saved-msg">
            <Check className="w-3.5 h-3.5" /> Saved · all projects now use this format.
          </span>
        )}
      </div>
    </section>
  );
}

function NomLockField({ label, hint, children }) {
  return (
    <div>
      <p className="text-[10.5px] uppercase tracking-wider text-zinc-500 mb-1">{label}</p>
      {children}
      {hint && <p className="text-[10px] text-zinc-600 mt-1">{hint}</p>}
    </div>
  );
}


